import React from 'react';
import {AbsoluteFill, Audio, Img, staticFile, spring, useCurrentFrame, useVideoConfig} from 'remotion';

/**
 * विष्णु स्मृति — the three-prohibition beat of गोत्र EP01.
 * Audio slice = 326.75s → 333.40s of the Charon narration; word stamps below are
 * the real WTS values (gotra_ep01_wts2.json) minus that 326.75 offset.
 *
 * LAWS (sanatan-decode-motion): animate ON TWOS · nothing fades · object permanence
 * (once a thing arrives it never leaves) · the background never moves · one bold move per beat.
 * The last ~1.3s holds the COMPLETE foreground — the end frame is the source image exactly.
 */
const T0 = 326.75;
const W = ['pothi', 'lines', 'panelL', 'seal1', 'panelM', 'seal2', 'panelR', 'seal3'] as const;
const AT: Record<(typeof W)[number], number> = {
  pothi: 327.001,   // विष्णु
  lines: 327.782,   // तीन
  panelL: 329.603,  // समान (गोत्र)
  seal1: 330.303,   // वर्जित
  panelM: 330.984,  // समान (प्रवर)
  seal2: 331.644,   // वर्जित
  panelR: 332.365,  // सपिंड
  seal3: 332.745,   // वर्जित
};

const src = (n: string) => staticFile(`sanatan/vsflow/${n}.png`);

const Layer: React.FC<{name: string; style?: React.CSSProperties}> = ({name, style}) => (
  <Img src={src(name)} style={{position: 'absolute', inset: 0, width: '100%', height: '100%', ...style}} />
);

export const VishnuSmritiFlow: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const f = Math.floor(frame / 2) * 2;                       // ON TWOS
  const at = (k: keyof typeof AT) => (AT[k] - T0) * fps;
  const since = (k: keyof typeof AT) => f - at(k);
  const inn = (k: keyof typeof AT, damping = 16, mass = 0.7) =>
    spring({frame: Math.max(0, since(k)), fps, config: {damping, mass, stiffness: 190}});
  const live = (k: keyof typeof AT) => since(k) >= 0;

  const p = inn('pothi', 18, 0.9);
  const lines = Math.min(1, Math.max(0, since('lines') / (fps * 1.35)));

  const panel = (k: 'panelL' | 'panelM' | 'panelR', originX: string) => {
    if (!live(k)) return null;
    const s = inn(k, 15, 0.8);
    return (
      <Layer
        name={k}
        style={{
          transform: `translateY(${(1 - s) * 300}px) scale(${0.965 + 0.035 * s})`,
          transformOrigin: `${originX} 82%`,
        }}
      />
    );
  };

  const seal = (k: 'seal1' | 'seal2' | 'seal3', originX: string) => {
    if (!live(k)) return null;
    const s = inn(k, 11, 0.55);
    return (
      <Layer
        name={k}
        style={{
          transform: `scale(${2.35 - 1.35 * s}) rotate(${-14 * (1 - s)}deg)`,
          transformOrigin: `${originX} 52%`,
        }}
      />
    );
  };

  return (
    <AbsoluteFill style={{backgroundColor: '#F4DFBC'}}>
      <Audio src={staticFile('sanatan/vsflow/vs_line.mp3')} />

      {/* the ground never moves */}
      <Layer name="vs_bg_plate" />
      <Layer name="static" />

      {/* विष्णु स्मृति — the pothi drops in and stays */}
      <Layer
        name="pothi"
        style={{
          transform: `translateY(${(1 - p) * -560}px) scale(${1.05 - 0.05 * p})`,
          transformOrigin: '50% 20%',
        }}
      />

      {/* the branch bus draws outward from the knot */}
      {lines > 0 ? (
        <Layer name="lines" style={{clipPath: `inset(0 ${50 - 50 * lines}% 0 ${50 - 50 * lines}%)`}} />
      ) : null}

      {panel('panelL', '16.7%')}
      {seal('seal1', '17.1%')}
      {panel('panelM', '50%')}
      {seal('seal2', '50%')}
      {panel('panelR', '83.5%')}
      {seal('seal3', '82.6%')}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
